// src/components/Organizations/Marketing/CampaignsTable.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { collection, getDocs, deleteDoc, doc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import SocialMediaCampaignCreator from './SocialMediaCampaignCreator';

interface CampaignsTableProps {
  organizationId: string;
  eventId: string;
  eventDetails: any;
}

const CampaignsTable: React.FC<CampaignsTableProps> = ({
  organizationId,
  eventId,
  eventDetails
}) => {
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<any | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, [organizationId, eventId]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const campaignsRef = collection(db, 'organizations', organizationId, 'marketingCampaigns');
      const q = query(
        campaignsRef, 
        where('eventId', '==', eventId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const fetchedCampaigns: any[] = [];
      
      querySnapshot.forEach((doc) => {
        fetchedCampaigns.push({ id: doc.id, ...doc.data() });
      });
      
      setCampaigns(fetchedCampaigns);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setError('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCampaign = (campaign: any) => {
    setSelectedCampaign(campaign);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedCampaign(null);
    // Refresh campaigns list after edit
    fetchCampaigns();
  };

  const handleDeleteClick = (campaign: any) => {
    setSelectedCampaign(campaign);
    setOpenDeleteDialog(true);
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setSelectedCampaign(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCampaign) return;
    
    setDeleteLoading(true);
    try {
      // Delete the campaign from Firestore
      await deleteDoc(doc(db, 'organizations', organizationId, 'marketingCampaigns', selectedCampaign.id));
      
      // Update the local state
      setCampaigns(campaigns.filter(c => c.id !== selectedCampaign.id));
      
      setOpenDeleteDialog(false);
      setSelectedCampaign(null);
    } catch (err) {
      console.error('Error deleting campaign:', err);
      setError('Failed to delete campaign');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'facebook': return <FacebookIcon style={{ color: '#1877F2' }} />;
      case 'instagram': return <InstagramIcon style={{ color: '#E4405F' }} />;
      case 'twitter': return <TwitterIcon style={{ color: '#1DA1F2' }} />;
      case 'linkedin': return <LinkedInIcon style={{ color: '#0A66C2' }} />;
      default: return null;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Social Media Campaigns</Typography>
        <Button 
          variant="contained" 
          startIcon={<AutoAwesomeIcon />}
          onClick={() => setOpenEditDialog(true)}
        >
          Create New Campaign
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : campaigns.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>No Campaigns Found</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Create your first social media campaign to promote your event across multiple platforms.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AutoAwesomeIcon />}
            onClick={() => setOpenEditDialog(true)}
          >
            Create Campaign
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Campaign Name</TableCell>
                <TableCell>Platforms</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Posts</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {campaign.platforms.map((platform: string) => (
                        <Box key={platform} sx={{ mr: 0.5 }}>
                          {getPlatformIcon(platform)}
                        </Box>
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {campaign.createdAt && new Date(campaign.createdAt.seconds * 1000).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={campaign.status.toUpperCase()} 
                      size="small" 
                      color={campaign.status === 'published' ? 'success' : 'default'} 
                    />
                  </TableCell>
                  <TableCell>{campaign.posts?.length || 0}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => handleEditCampaign(campaign)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteClick(campaign)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Edit Dialog */}
      <SocialMediaCampaignCreator
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        organizationId={organizationId}
        eventId={eventId}
        eventDetails={eventDetails}
        existingCampaign={selectedCampaign}
      />
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Campaign</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the campaign "{selectedCampaign?.name}"? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>Cancel</Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            disabled={deleteLoading}
            startIcon={deleteLoading && <CircularProgress size={20} />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignsTable;